import React, { useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../api';
import { Box, Typography } from '@mui/material';
import Spinner from '../components/Spinner';
import { getRedirectPathByEducationLevel } from '../utils/redirectUtils';
import SessionReplacementDialog, { SessionReplacementProof, SessionReplacementProofType } from '../components/SessionReplacementDialog';

/**
 * AuthCallback component handles Google OAuth callback
 * Extracts token from URL query params and sets it in auth context
 */
const AuthCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [replacementChallenge, setReplacementChallenge] = React.useState<string | null>(null);
  const [replacementProofType, setReplacementProofType] = React.useState<SessionReplacementProofType>('oauth');
  const [confirmingReplacement, setConfirmingReplacement] = React.useState(false);
  const [replacementError, setReplacementError] = React.useState<string | null>(null);
  const navigate = useNavigate();
  const { setToken, updateUser } = useAuth();

  useEffect(() => {
    const handleOAuthCallback = async () => {
      try {
        const token = searchParams.get('token');
        const challenge = searchParams.get('replacementChallenge');
        const userId = searchParams.get('userId');
        const email = searchParams.get('email');
        const name = searchParams.get('name');

        if (challenge) {
          setReplacementChallenge(challenge);
          setReplacementProofType((searchParams.get('replacementProof') as SessionReplacementProofType) || 'oauth');
          return;
        }

        if (!token) {
          console.error('No token in OAuth callback');
          navigate('/login?error=no_token');
          return;
        }

        // Create user object from OAuth response
        const userData = {
          _id: userId || '',
          email: email || '',
          name: name || '',
        };

        // Store token in localStorage
        localStorage.setItem('auth_token', token);
        
        // Persist token and set in context
        localStorage.setItem('auth_token', token);
        setToken(token);

        // Try to fetch authoritative profile (may include premium flags)
        try {
          const res = await api.get('/users/profile');
          if (res?.data?.data) {
            const profile = res.data.data;
            try { localStorage.setItem('user', JSON.stringify(profile)); } catch (e) {}
            if (updateUser) updateUser(profile as any);
            
            // Navigate to appropriate page based on education level (replace history)
            const redirectPath = getRedirectPathByEducationLevel(profile?.educationLevel);
            navigate(redirectPath, { replace: true });
          } else {
            // fallback: store minimal user data
            try { localStorage.setItem('user', JSON.stringify(userData)); } catch (e) {}
            if (updateUser) updateUser(userData as any);
            navigate('/', { replace: true });
          }
        } catch (err) {
          // On failure, fallback to minimal user payload but keep token
          console.warn('[AuthCallback] Failed to fetch profile after OAuth:', err);
          try { localStorage.setItem('user', JSON.stringify(userData)); } catch (e) {}
          if (updateUser) updateUser(userData as any);
          navigate('/', { replace: true });
        }
      } catch (error) {
        console.error('OAuth callback error:', error);
        navigate('/login?error=callback_failed');
      }
    };

    handleOAuthCallback();
  }, [searchParams, navigate, setToken, updateUser]);

  const confirmReplacement = async (proof: SessionReplacementProof) => {
    if (!replacementChallenge) return;
    setConfirmingReplacement(true);
    setReplacementError(null);
    try {
      const response = await api.post('/auth/confirm-session-replacement', { challengeToken: replacementChallenge, ...proof });
      const token = response.data?.token;
      const profile = response.data?.data;
      if (!token || !profile) throw new Error('Invalid sign-in response');
      localStorage.setItem('auth_token', token);
      setToken(token);
      updateUser(profile);
      navigate(getRedirectPathByEducationLevel(profile.educationLevel), { replace: true });
    } catch (error: any) {
      if (error?.response?.data?.code === 'SESSION_REPLACEMENT_REQUIRED') {
        setReplacementChallenge(error.response.data.challengeToken);
        setReplacementProofType(error.response.data.proofType || replacementProofType);
      } else if (error?.response?.data?.code === 'INVALID_CONFIRMATION_CREDENTIALS') {
        setReplacementError(error.response.data.message || 'Your authentication proof is incorrect.');
      } else if (error?.response?.data?.code === 'SESSION_REPLACEMENT_EXPIRED') {
        setReplacementChallenge(null);
        navigate('/login?error=session_confirmation_expired', { replace: true });
      } else {
        setReplacementError(error?.response?.data?.message || 'Could not sign in on this device. Please try again.');
      }
    } finally {
      setConfirmingReplacement(false);
    }
  };

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', gap: 2 }}>
        <Spinner />
        <Typography variant="body1">Processing authentication...</Typography>
      </Box>
      <SessionReplacementDialog
        open={Boolean(replacementChallenge)}
        proofType={replacementProofType}
        loading={confirmingReplacement}
        error={replacementError}
        onConfirm={confirmReplacement}
        onCancel={() => navigate('/login', { replace: true })}
      />
    </>
  );
};

export default AuthCallback;
